export const PURCHASE_STATUS = Object.freeze({
  PENDING: "PENDING",
  APPROVED: "APPROVED",
});

export const PURCHASE_STATUS_LABELS = Object.freeze({
  [PURCHASE_STATUS.PENDING]: "Pendiente",
  [PURCHASE_STATUS.APPROVED]: "Aprobado",
});

const INTEGER_PATTERN = /^\d+$/;
const MONEY_PATTERN = /^\d+(?:[.,]\d{1,2})?$/;

function asNumber(value, fallback = 0) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function asNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function asDecimalString(value, fallback = "0.00") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

export function normalizePurchaseLine(line = {}) {
  return {
    ...line,
    id: asNumber(line.id),
    purchaseItemId: asNumber(line.purchaseItemId),
    ingredientId: asNumber(line.ingredientId),
    ingredientName: line.ingredientName || "",
    requestedQuantity: asNumber(line.requestedQuantity),
    receivedQuantity: asNullableNumber(line.receivedQuantity),
    newUnitCost:
      line.newUnitCost === null || line.newUnitCost === undefined
        ? null
        : String(line.newUnitCost),
    currentUnitCostSnapshot: asDecimalString(line.currentUnitCostSnapshot),
    approvedUnitCost:
      line.approvedUnitCost === null || line.approvedUnitCost === undefined
        ? null
        : String(line.approvedUnitCost),
    applicableUnitCost: asDecimalString(
      line.applicableUnitCost,
      asDecimalString(line.currentUnitCostSnapshot)
    ),
    requestedTotal: asDecimalString(line.requestedTotal),
    receivedTotal:
      line.receivedTotal === null || line.receivedTotal === undefined
        ? null
        : String(line.receivedTotal),
  };
}

export function normalizePurchase(purchase = {}) {
  return {
    ...purchase,
    id: asNumber(purchase.id),
    supplier: {
      id: asNumber(purchase.supplier?.id),
      name: purchase.supplier?.name || "",
      phone: purchase.supplier?.phone ?? null,
    },
    status:
      purchase.status === PURCHASE_STATUS.APPROVED
        ? PURCHASE_STATUS.APPROVED
        : PURCHASE_STATUS.PENDING,
    total: asDecimalString(purchase.total),
    lines: Array.isArray(purchase.lines)
      ? purchase.lines.map(normalizePurchaseLine)
      : [],
  };
}

export function normalizePurchaseList(response) {
  const data = Array.isArray(response?.data)
    ? response.data.map(normalizePurchase)
    : [];
  return {
    data,
    meta: {
      page: asNumber(response?.meta?.page, 1),
      limit: asNumber(response?.meta?.limit, 20),
      totalItems: asNumber(response?.meta?.totalItems),
      totalPages: asNumber(response?.meta?.totalPages),
      hasNextPage: Boolean(response?.meta?.hasNextPage),
      hasPreviousPage: Boolean(response?.meta?.hasPreviousPage),
    },
  };
}

export function normalizePurchaseSuppliers(response) {
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  return rows
    .filter((supplier) => supplier?.id && supplier?.name)
    .map((supplier) => ({
      id: asNumber(supplier.id),
      name: String(supplier.name),
    }))
    .sort((left, right) =>
      left.name.localeCompare(right.name, "es", { sensitivity: "base" })
    );
}

export function purchaseLineToDraft(line) {
  const normalized = normalizePurchaseLine(line);
  return {
    purchaseItemId: normalized.purchaseItemId,
    ingredientId: normalized.ingredientId,
    ingredientName: normalized.ingredientName,
    brand: normalized.brand || "",
    presentation: normalized.presentation || "",
    purchaseUnit: normalized.purchaseUnit || "",
    currentUnitCostSnapshot: normalized.currentUnitCostSnapshot,
    requestedQuantity: String(normalized.requestedQuantity),
    receivedQuantity:
      normalized.receivedQuantity === null
        ? ""
        : String(normalized.receivedQuantity),
    newUnitCost:
      normalized.newUnitCost === null ? "" : String(normalized.newUnitCost),
  };
}

export function parseNonNegativeInteger(value, { optional = false } = {}) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return optional ? null : undefined;
  if (!INTEGER_PATTERN.test(normalized)) return undefined;

  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

export function parseNonNegativeMoney(value, { optional = false } = {}) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return optional ? null : undefined;
  if (!MONEY_PATTERN.test(normalized)) return undefined;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

export function effectiveUnitCost(line) {
  const newUnitCost = parseNonNegativeMoney(line?.newUnitCost, {
    optional: true,
  });
  return newUnitCost === null
    ? parseNonNegativeMoney(line?.currentUnitCostSnapshot)
    : newUnitCost;
}

export function calculateLineTotals(line) {
  const requestedQuantity = parseNonNegativeInteger(line?.requestedQuantity);
  const receivedQuantity = parseNonNegativeInteger(line?.receivedQuantity, {
    optional: true,
  });
  const unitCost = effectiveUnitCost(line);

  return {
    requestedTotal:
      requestedQuantity === undefined || unitCost === undefined
        ? null
        : (requestedQuantity * unitCost).toFixed(2),
    receivedTotal:
      receivedQuantity === null ||
      receivedQuantity === undefined ||
      unitCost === undefined
        ? null
        : (receivedQuantity * unitCost).toFixed(2),
  };
}

export function validatePurchaseLines(lines) {
  const errors = {};
  const seenIds = new Set();

  lines.forEach((line) => {
    const lineErrors = {};
    const purchaseItemId = Number(line.purchaseItemId);
    if (!purchaseItemId || seenIds.has(purchaseItemId)) {
      lineErrors.purchaseItemId = "El ítem no es válido o está duplicado";
    }
    seenIds.add(purchaseItemId);

    if (parseNonNegativeInteger(line.requestedQuantity) === undefined) {
      lineErrors.requestedQuantity =
        "El pedido es obligatorio y debe ser un entero no negativo";
    }
    if (
      parseNonNegativeInteger(line.receivedQuantity, { optional: true }) ===
      undefined
    ) {
      lineErrors.receivedQuantity =
        "El ingreso debe ser un entero no negativo";
    }
    if (
      parseNonNegativeMoney(line.newUnitCost, { optional: true }) === undefined
    ) {
      lineErrors.newUnitCost =
        "El precio nuevo debe ser un importe no negativo con hasta 2 decimales";
    }

    if (Object.keys(lineErrors).length) errors[purchaseItemId || "invalid"] = lineErrors;
  });

  if (!lines.length) errors.form = "La compra debe contener al menos un ítem";
  return errors;
}

export function buildUpdatePurchasePayload(lines) {
  return {
    lines: lines.map((line) => ({
      purchaseItemId: Number(line.purchaseItemId),
      requestedQuantity: parseNonNegativeInteger(line.requestedQuantity),
      receivedQuantity: parseNonNegativeInteger(line.receivedQuantity, {
        optional: true,
      }),
      newUnitCost: parseNonNegativeMoney(line.newUnitCost, { optional: true }),
    })),
  };
}

export function createSupplierOrderDraft(supplier) {
  return {
    supplierId: asNumber(supplier?.id),
    supplierName: String(supplier?.name || ""),
    lines: [],
  };
}

export function supplierOrderDraftHasData(draft) {
  return Array.isArray(draft?.lines) && draft.lines.length > 0;
}

export function validateCreatePurchaseOrder(supplierDrafts) {
  if (!supplierDrafts.length) {
    return {
      formError: "Seleccioná al menos un proveedor.",
      supplierErrors: {},
      emptyDrafts: [],
      validDrafts: [],
    };
  }

  const emptyDrafts = supplierDrafts.filter(
    (draft) => !Array.isArray(draft.lines) || !draft.lines.length
  );
  const validDrafts = supplierDrafts.filter(
    (draft) => Array.isArray(draft.lines) && draft.lines.length
  );

  if (!validDrafts.length) {
    return {
      formError: "Agregá al menos un ítem a uno de los proveedores.",
      supplierErrors: {},
      emptyDrafts,
      validDrafts,
    };
  }

  const supplierErrors = {};
  validDrafts.forEach((draft) => {
    const errors = validatePurchaseLines(draft.lines);
    if (Object.keys(errors).length) supplierErrors[draft.supplierId] = errors;
  });

  return {
    formError: Object.keys(supplierErrors).length
      ? "Revisá los campos marcados antes de crear el pedido."
      : "",
    supplierErrors,
    emptyDrafts,
    validDrafts,
  };
}

export function buildCreatePurchaseOrderPayload(supplierDrafts) {
  return {
    purchases: supplierDrafts.map((draft) => ({
      supplierId: Number(draft.supplierId),
      lines: buildUpdatePurchasePayload(draft.lines).lines,
    })),
  };
}

export function createPurchaseOrderDraftSignature(supplierDrafts) {
  return JSON.stringify(
    supplierDrafts.map((draft) => ({
      supplierId: Number(draft.supplierId),
      lines: JSON.parse(purchaseDraftSignature(draft.lines)),
    }))
  );
}

export function purchaseDraftSignature(lines) {
  return JSON.stringify(
    lines.map((line) => ({
      purchaseItemId: Number(line.purchaseItemId),
      requestedQuantity: String(line.requestedQuantity ?? "").trim(),
      receivedQuantity: String(line.receivedQuantity ?? "").trim(),
      newUnitCost: String(line.newUnitCost ?? "").trim().replace(",", "."),
    }))
  );
}

export function normalizeSupplierPurchaseItem(item = {}) {
  return {
    id: asNumber(item.id),
    ingredientId: asNumber(item.ingredient?.id),
    ingredientName: item.ingredient?.name || "",
    categoryId: asNumber(item.ingredient?.category?.id),
    categoryName: item.ingredient?.category?.name || "",
    brand: item.brand || "",
    presentation: item.presentation || "",
    purchaseUnit: item.purchaseUnit || "",
    currentUnitCost: asDecimalString(item.cost),
  };
}

export function supplierPurchaseItemToDraft(item) {
  const normalized = item?.ingredientName
    ? item
    : normalizeSupplierPurchaseItem(item);
  return {
    purchaseItemId: normalized.id ?? normalized.purchaseItemId,
    ingredientId: normalized.ingredientId,
    ingredientName: normalized.ingredientName,
    categoryId: normalized.categoryId,
    categoryName: normalized.categoryName,
    brand: normalized.brand,
    presentation: normalized.presentation,
    purchaseUnit: normalized.purchaseUnit,
    currentUnitCostSnapshot:
      normalized.currentUnitCost || normalized.currentUnitCostSnapshot || "0.00",
    requestedQuantity: "",
    receivedQuantity: "",
    newUnitCost: "",
  };
}

export function buildVisiblePurchaseItemRows({
  lines,
  availableItems,
  viewMode,
  filtersActive = false,
}) {
  const selectedById = new Map(
    lines.map((line) => [Number(line.purchaseItemId), line])
  );
  const availableById = new Map(
    availableItems.map((item) => [Number(item.id), item])
  );
  const selectedLines = filtersActive
    ? lines.filter((line) => availableById.has(Number(line.purchaseItemId)))
    : lines;
  const selectedRows = selectedLines.map((line) => {
    const available = availableById.get(Number(line.purchaseItemId));
    return {
      ...line,
      categoryId: available?.categoryId || line.categoryId,
      categoryName: available?.categoryName || line.categoryName,
      isSelected: true,
    };
  });

  if (viewMode === "selected") return selectedRows;

  const unselectedRows = availableItems
    .filter((item) => !selectedById.has(Number(item.id)))
    .map((item) => ({
      ...supplierPurchaseItemToDraft(item),
      isSelected: false,
    }));

  return [...selectedRows, ...unselectedRows];
}
